import axios from "axios";
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";

const colors = {
  red: "#a20000",
  redDark: "#700000",
  redSoft: "#fbebeb",
  ink: "#171717",
  muted: "#666666",
  line: "#e8e8e8",
  surface: "#f7f7f7",
  white: "#ffffff",
};

function useViewportWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
}

const container = (max = 1200) => ({
  width: "100%",
  maxWidth: max,
  margin: "0 auto",
  padding: "0 20px",
});

const btnBase = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 9,
  padding: "13px 22px",
  borderRadius: 999,
  fontWeight: 800,
  border: "1px solid transparent",
  cursor: "pointer",
  textDecoration: "none",
  fontSize: 15,
};

const btnPrimary = {
  ...btnBase,
  background: colors.red,
  color: "#fff",
};

const btnSecondary = {
  ...btnBase,
  borderColor: colors.red,
  color: colors.red,
  background: "#fff",
};

const card = {
  background: "#fff",
  border: `1px solid ${colors.line}`,
  borderRadius: 24,
  padding: 28,
  boxShadow: "0 8px 24px rgba(0,0,0,.04)",
};

const specRow = {
  display: "flex",
  justifyContent: "space-between",
  padding: "12px 0",
  borderBottom: `1px solid ${colors.line}`,
  fontSize: 15,
};

const kicker = {
  color: colors.red,
  fontSize: 13,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: 1,
};

const eyebrow = {
  display: "inline-flex",
  padding: "7px 14px",
  borderRadius: 999,
  background: colors.redSoft,
  color: colors.red,
  fontWeight: 900,
  fontSize: 12,
  letterSpacing: 0.8,
  textTransform: "uppercase",
};

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${colors.line}` }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          background: "none",
          border: 0,
          padding: "22px 0",
          textAlign: "left",
          font: "inherit",
          fontWeight: 850,
          fontSize: 18,
          display: "flex",
          justifyContent: "space-between",
          cursor: "pointer",
          color: colors.ink,
        }}
      >
        {q}
        <span style={{ color: colors.red, fontSize: 20 }}>{open ? "−" : "+"}</span>
      </button>
      {open && <div style={{ padding: "0 0 22px", color: colors.muted }}>{a}</div>}
    </div>
  );
}

const hybridFaqs = [
  {
    q: "What is a hybrid inverter?",
    a: "A hybrid inverter combines a solar inverter and battery inverter in one unit. It can convert solar DC to AC for your loads, charge a battery bank, and intelligently manage power between solar, battery, and the grid.",
  },
  {
    q: "Can a hybrid inverter work without a battery?",
    a: "Yes. Most hybrid inverters can operate in on-grid mode without a battery, functioning like a standard grid-tied inverter. Adding a battery later enables backup and self-consumption optimisation.",
  },
  {
    q: "Does it provide backup during grid outages?",
    a: "Yes. When paired with a compatible battery, a hybrid inverter can supply backup power to essential loads during grid outages. The backup capacity depends on the battery size and inverter rating.",
  },
  {
    q: "What battery types are supported?",
    a: "Our hybrid inverters support lithium-ion (LiFePO₄) and lead-acid battery banks. The battery compatibility depends on the specific model and its DC voltage range.",
  },
  {
    q: "Can I monitor my hybrid inverter remotely?",
    a: "Yes. Both models include Wi-Fi and RS485 communication for real-time monitoring of solar generation, battery state of charge, grid consumption, and system performance.",
  },
];

const overviewCards = [
  { icon: "↗", title: "High Efficiency", text: "Maximum efficiency of up to 97.5% and MPPT efficiency above 99%." },
  { icon: "⌁", title: "Remote Monitoring", text: "Wi-Fi and RS485 communication for compatible monitoring systems." },
  { icon: "☂", title: "IP65 Enclosure", text: "Protection against dust and water ingress for suitable installation conditions." },
  { icon: "◌", title: "Quiet Operation", text: "Natural-convection cooling with operating noise below 25 dB." },
  { icon: "▣", title: "Compact Design", text: "Wall-mounted construction for easier handling and installation." },
  { icon: "✓", title: "Grid Ready", text: "Single-phase architecture for residential rooftop applications." },
];

const applicationCards = [
  { title: "Residential Rooftops", text: "For independent houses and residential buildings." },
  { title: "PM Surya Ghar Installations", text: "For appropriately designed systems, subject to current scheme and DISCOM requirements." },
  { title: "Shops and Offices", text: "For properties with regular daytime electricity consumption." },
  { title: "Clinics and Institutions", text: "For smaller facilities seeking rooftop solar savings." },
  { title: "Retail Outlets", text: "Efficient solar conversion for daily commercial loads." },
  { title: "Small Commercial Properties", text: "Suitable for appropriately sized single-phase systems." },
];

const specRows = [
  ["Rated AC output power", "3 kW", "5 kW"],
  ["Maximum DC input power", "3.9 kW", "6.5 kW"],
  ["Maximum DC input voltage", "550 V", "550 V"],
  ["Start-up voltage", "80 V", "80 V"],
  ["MPPT operating range", "70–500 V", "70–500 V"],
  ["Maximum DC input current", "20 A", "20 A"],
  ["Maximum AC apparent power", "3.3 kVA", "5.5 kVA"],
  ["Maximum efficiency", "97.5%", "97.5%"],
  ["Euro efficiency", "97.3%", "97.3%"],
  ["Communication", "Wi-Fi / RS485", "Wi-Fi / RS485"],
  ["Protection rating", "IP65", "IP65"],
  ["Operating noise", "< 25 dB", "< 25 dB"],
  ["Dimensions", "290 × 290 × 120 mm", "347 × 386 × 167 mm"],
  ["Weight", "≤5 kg", "9 kg"],
];

const certifications = [
  { src: "/inverter01.webp", alt: "BIS certification", label: "BIS" },
  { src: "/inverter02.webp", alt: "IEC certification", label: "IEC" },
];

const steps = [
  { n: "1", title: "Solar Generation", text: "PV modules generate DC power from sunlight." },
  { n: "2", title: "MPPT Optimisation", text: "The inverter extracts maximum power from the array." },
  { n: "3", title: "DC to AC Conversion", text: "DC is converted to grid-compatible AC for your loads." },
  { n: "4", title: "Grid Sync", text: "Excess power flows to the grid, where permitted." },
];

export default function HybridInverterPage() {
  const width = useViewportWidth();
  const isTablet = width <= 980;
  const isMobile = width <= 640;

  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [formData, setFormData] = useState({ fullName: "", email: "", phone: "", model: "", remark: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e) => e.key === "Escape" && setLightboxSrc(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxSrc]);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, []);

  const handleChange = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const validate = () => {
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) { alert("Enter a valid email"); return false; }
    if (!/^[6-9]\d{9}$/.test(formData.phone.replace(/\s+/g, ""))) { alert("Enter a valid 10-digit mobile"); return false; }
    if (formData.model === "") { alert("Select a model"); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setIsSubmitting(true);
      const utm = localStorage.getItem("utm_data") || null;
      const res = await axios.post("https://gautamsolar.us/submit-inverter", { ...formData, utm });
      if (res?.data?.success) {
        alert("Form submitted successfully!");
        localStorage.removeItem("utm_data");
        setFormData({ fullName: "", email: "", phone: "", model: "", remark: "" });
      }
    } catch (er) { console.log(er); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div style={{ fontFamily: "Inter, Arial, sans-serif", color: colors.ink, background: "#fff", lineHeight: 1.6 }}>
      <Helmet>
        <title>Hybrid Solar Inverters | 3kW & 5kW Solar + Battery Inverters - Gautam Solar</title>
        <meta name="description" content="Gautam Solar hybrid inverters combine solar and battery management in 3kW and 5kW models. Dual MPPT, backup switchover, Wi-Fi/RS485 monitoring." />
        <link rel="canonical" href="https://gautamsolar.com/inverter/hybrid/" />
      </Helmet>

      {/* Hero — centered, soft background */}
      <header style={{ background: colors.surface, borderBottom: `1px solid ${colors.line}` }}>
        <div style={{ ...container(), padding: isMobile ? "56px 20px 48px" : "72px 20px 80px", textAlign: "center", maxWidth: 900, margin: "0 auto" }}>
          <span style={eyebrow}>Hybrid Inverter Series</span>
          <h1 style={{ fontSize: "clamp(38px,5vw,64px)", lineHeight: 1.04, margin: "20px 0 16px", letterSpacing: -2 }}>
            Solar + Battery Hybrid Inverters for Energy Independence
          </h1>
          <p style={{ fontSize: 18, color: colors.muted, maxWidth: 640, margin: "0 auto 28px" }}>
            Intelligent inverters that manage solar, battery, and grid power to maximise self-consumption and provide reliable backup during outages.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="#models" style={btnPrimary}>Explore Models</a>
            <a href="#specs" style={btnSecondary}>View Specifications</a>
          </div>
          <div style={{ marginTop: 44, background: "#fff", borderRadius: 24, padding: 20, border: `1px solid ${colors.line}`, maxWidth: 720, marginLeft: "auto", marginRight: "auto" }}>
            <img src="/inverter1.jpeg" alt="Gautam Solar hybrid inverter series" style={{ borderRadius: 16, width: "100%", maxHeight: 460, objectFit: "contain", display: "block" }} />
          </div>
        </div>
      </header>

      {/* How it works — 4-step strip */}
      <section style={{ padding: isMobile ? "56px 0" : "80px 0" }}>
        <div style={container()}>
          <div style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 44px" }}>
            <span style={kicker}>How It Works</span>
            <h2 style={{ fontSize: "clamp(30px,3.8vw,46px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              From sunlight to grid, in four steps
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: 20 }}>
            {steps.map((s) => (
              <div key={s.n} style={{ ...card, textAlign: "center" }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: colors.redSoft, color: colors.red, display: "grid", placeItems: "center", fontSize: 18, fontWeight: 900, margin: "0 auto 16px" }}>
                  {s.n}
                </div>
                <h3 style={{ fontSize: 18, margin: "0 0 8px" }}>{s.title}</h3>
                <p style={{ color: colors.muted, margin: 0, fontSize: 15 }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview — soft cards */}
      <section style={{ padding: isMobile ? "56px 0" : "80px 0", background: colors.surface }}>
        <div style={container()}>
          <div style={{ maxWidth: 720, marginBottom: 34 }}>
            <span style={kicker}>Product Overview</span>
            <h2 style={{ fontSize: "clamp(30px,3.8vw,46px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              Efficient power conversion for rooftop solar systems
            </h2>
            <p style={{ color: colors.muted }}>
              The series uses MPPT technology to optimise solar generation while supporting remote monitoring and dependable grid-connected operation.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(3,1fr)", gap: 20 }}>
            {overviewCards.map((c) => (
              <div key={c.title} style={card}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: colors.redSoft, color: colors.red, display: "grid", placeItems: "center", fontSize: 20, fontWeight: 900, marginBottom: 14 }}>
                  {c.icon}
                </div>
                <h3 style={{ fontSize: 20, margin: "0 0 8px" }}>{c.title}</h3>
                <p style={{ color: colors.muted, margin: 0 }}>{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Models — stacked vertical cards */}
      <section id="models" style={{ padding: isMobile ? "56px 0" : "80px 0" }}>
        <div style={container()}>
          <div style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 44px" }}>
            <span style={kicker}>Product Models</span>
            <h2 style={{ fontSize: "clamp(30px,3.8vw,46px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              Choose the right hybrid inverter for your needs
            </h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {[
              { model: "GSPL-3000XT", power: "3 kW", img: "/3KW-Inverter.webp", tagline: "Compact single-phase hybrid inverter for homes seeking solar savings with battery backup." },
              { model: "GSPL-5000XT", power: "5 kW", img: "/5KW-Inverter.webp", tagline: "Higher-capacity hybrid inverter for larger homes, shops and small commercial properties." },
            ].map((m) => (
              <article key={m.model} style={{ ...card, display: "grid", gridTemplateColumns: isTablet ? "1fr" : "1fr 1.4fr", gap: 30, alignItems: "center" }}>
                <div style={{ background: colors.surface, borderRadius: 18, padding: 18, display: "grid", placeItems: "center" }}>
                  <img src={m.img} alt={m.model} style={{ width: "100%", maxHeight: 260, objectFit: "contain", display: "block" }} />
                </div>
                <div>
                  <span style={eyebrow}>{m.power} Model</span>
                  <h3 style={{ fontSize: 28, margin: "12px 0 6px", letterSpacing: -0.8 }}>{m.model}</h3>
                  <p style={{ color: colors.muted, margin: "0 0 18px" }}>{m.tagline}</p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 20px" }}>
                    {[
                      ["Rated AC Output", m.power],
                      ["Max DC Input", m.power === "3 kW" ? "3.9 kW" : "6.5 kW"],
                      ["MPPT Range", "70–500 V"],
                      ["Maximum Efficiency", "97.5%"],
                      ["Protection", "IP65"],
                      ["Weight", m.power === "3 kW" ? "≤5 kg" : "9 kg"],
                    ].map(([k, v]) => (
                      <div key={k} style={specRow}>
                        <span style={{ color: colors.muted }}>{k}</span>
                        <span style={{ fontWeight: 800 }}>{v}</span>
                      </div>
                    ))}
                  </div>
                  <a href="#enquiry" style={{ ...btnPrimary, marginTop: 20 }}>Get a Quote</a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Specs — two side-by-side panels */}
      <section id="specs" style={{ padding: isMobile ? "56px 0" : "80px 0", background: colors.surface }}>
        <div style={container()}>
          <div style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 44px" }}>
            <span style={kicker}>Technical Comparison</span>
            <h2 style={{ fontSize: "clamp(30px,3.8vw,46px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              3 kW and 5 kW hybrid inverter specifications
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 22 }}>
            {["GSPL-3000XT", "GSPL-5000XT"].map((name, mi) => (
              <div key={name} style={{ ...card, background: "#fff" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                  <h3 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>{name}</h3>
                  <span style={{ ...eyebrow, fontSize: 11 }}>{mi === 0 ? "3 kW" : "5 kW"}</span>
                </div>
                {specRows.map((r, i) => (
                  <div key={r[0]} style={{ ...specRow, borderBottom: i === specRows.length - 1 ? "none" : `1px solid ${colors.line}` }}>
                    <span style={{ color: colors.muted }}>{r[0]}</span>
                    <span style={{ fontWeight: 800 }}>{r[mi + 1]}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications */}
      <section style={{ padding: isMobile ? "56px 0" : "80px 0" }}>
        <div style={container()}>
          <div style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 44px" }}>
            <span style={kicker}>Applications</span>
            <h2 style={{ fontSize: "clamp(30px,3.8vw,46px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              Ideal for homes and businesses needing backup power
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(3,1fr)", gap: 20 }}>
            {applicationCards.map((c) => (
              <div key={c.title} style={card}>
                <h3 style={{ fontSize: 20, margin: "0 0 10px" }}>{c.title}</h3>
                <p style={{ color: colors.muted, margin: 0 }}>{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section style={{ padding: isMobile ? "56px 0" : "80px 0", background: colors.surface }}>
        <div style={container()}>
          <div style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 34px" }}>
            <span style={kicker}>Certifications</span>
            <h2 style={{ fontSize: "clamp(30px,3.8vw,46px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              Certified for quality and safety
            </h2>
            <p style={{ color: colors.muted, margin: 0 }}>Tap any certificate to view it larger.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20 }}>
            {certifications.map((c) => (
              <button key={c.src} type="button" onClick={() => setLightboxSrc(c)}
                style={{ background: "#fff", border: `1px solid ${colors.line}`, borderRadius: 22, padding: 18, cursor: "zoom-in", display: "flex", flexDirection: "column", gap: 12, font: "inherit", textAlign: "left" }}>
                <div style={{ background: colors.surface, borderRadius: 14, minHeight: 320, display: "grid", placeItems: "center", padding: 12 }}>
                  <img src={c.src} alt={c.alt} style={{ maxWidth: "100%", maxHeight: 440, objectFit: "contain" }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 800, color: colors.red, textTransform: "uppercase", letterSpacing: 0.6 }}>{c.label} — Click to enlarge</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: isMobile ? "56px 0" : "80px 0" }}>
        <div style={{ ...container(), maxWidth: 780, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 34 }}>
            <span style={kicker}>Frequently Asked Questions</span>
            <h2 style={{ fontSize: "clamp(30px,3.8vw,46px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              Hybrid inverter FAQs
            </h2>
          </div>
          <div style={{ borderTop: `1px solid ${colors.line}` }}>
            {hybridFaqs.map((f) => <FaqItem key={f.q} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Enquiry — soft card */}
      <section id="enquiry" style={{ padding: isMobile ? "56px 0" : "80px 0", background: colors.surface }}>
        <div style={container()}>
          <div style={{ ...card, maxWidth: 900, margin: "0 auto", display: "grid", gridTemplateColumns: isTablet ? "1fr" : "1fr 1fr", gap: 36, alignItems: "center" }}>
            <div>
              <span style={kicker}>Product Enquiry</span>
              <h2 style={{ fontSize: "clamp(26px,3vw,36px)", margin: "12px 0 12px", letterSpacing: -1 }}>
                Find the right hybrid inverter for your needs
              </h2>
              <p style={{ color: colors.muted, margin: 0 }}>
                Share your requirement and our team will get back with the right model and sizing recommendation.
              </p>
            </div>
            <form onSubmit={handleSubmit} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              <input name="fullName" value={formData.fullName} onChange={handleChange} placeholder="Full name"
                style={{ background: colors.surface, border: `1px solid ${colors.line}`, padding: "13px 15px", borderRadius: 12, font: "inherit" }} />
              <input name="phone" value={formData.phone} onChange={handleChange} placeholder="Mobile number"
                style={{ background: colors.surface, border: `1px solid ${colors.line}`, padding: "13px 15px", borderRadius: 12, font: "inherit" }} />
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email address"
                style={{ gridColumn: "1/-1", background: colors.surface, border: `1px solid ${colors.line}`, padding: "13px 15px", borderRadius: 12, font: "inherit" }} />
              <select name="model" value={formData.model} onChange={handleChange}
                style={{ gridColumn: "1/-1", background: colors.surface, border: `1px solid ${colors.line}`, padding: "13px 15px", borderRadius: 12, font: "inherit" }}>
                <option value="">Select model</option>
                <option value="3kW">3 kW — GSPL-3000XT</option>
                <option value="5kW">5 kW — GSPL-5000XT</option>
                <option value="not-sure">Not sure</option>
              </select>
              <textarea name="remark" value={formData.remark} onChange={handleChange} placeholder="Project details"
                style={{ gridColumn: "1/-1", background: colors.surface, border: `1px solid ${colors.line}`, padding: "13px 15px", borderRadius: 12, font: "inherit", minHeight: 100, resize: "vertical" }} />
              <button type="submit" disabled={isSubmitting}
                style={{ ...btnPrimary, gridColumn: "1/-1", opacity: isSubmitting ? 0.7 : 1 }}>
                {isSubmitting && <span style={{ width: 14, height: 14, border: `2px solid ${colors.redSoft}`, borderTopColor: "#fff", borderRadius: "50%", animation: "spin .7s linear infinite" }} />}
                {isSubmitting ? "Submitting..." : "Submit Enquiry"}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxSrc && (
        <div onClick={() => setLightboxSrc(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 9999, cursor: "zoom-out" }}>
          <button onClick={(e) => { e.stopPropagation(); setLightboxSrc(null); }} aria-label="Close"
            style={{ position: "absolute", top: 20, right: 20, width: 44, height: 44, borderRadius: "50%", border: "1px solid #444", background: "#1a1a1a", color: "#fff", fontSize: 22, cursor: "pointer" }}>×</button>
          <img src={lightboxSrc.src} alt={lightboxSrc.alt} onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "95vw", maxHeight: "90vh", objectFit: "contain", background: "#fff", borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}